"use client";

import { useState, useEffect, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/themes/ui/sheet";
import { Button } from "@/components/themes/ui/button";
import { X, Check } from "lucide-react";
import { Checkbox } from "@/components/themes/ui/checkbox";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

type FilterOption = {
  value: string;
  label: string;
  count: number;
  subcategories?: FilterOption[];
  sortorder?: number;
};

type MobileFilterSheetProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: { category: FilterOption[]; [key: string]: FilterOption[] };
  selectedFilters: { [key: string]: string[] };
  selectedMainCategory: string | null;
  onFilterChange: (filterType: string, value: string, checked: boolean) => void;
  onClearAll: () => void;
  onApply: () => void;
  setCategorySlug: (slug: string) => void;
  // DO NOT ADD openPaths or onOpenPathsChange here
};

/* ------------------------------------------------------------------ */
/*  Mobile recursive tree node – SAME SMOOTH ANIMATION AS DESKTOP    */
/* ------------------------------------------------------------------ */
const CategoryTreeNodeMobile = ({
  node,
  path,
  depth = 0,
  selectedValue,
  openPaths,
  onPathChange,
  onSelect,
}: {
  node: FilterOption;
  path: string[];
  depth?: number;
  selectedValue: string | null;
  openPaths: string[];
  onPathChange: (paths: string[]) => void;
  onSelect: (value: string, newOpenPaths?: string[]) => void;
}) => {
  const pathKey = path.join("/");
  const isOpen = openPaths.includes(pathKey);
  const hasChildren = !!node.subcategories?.length;
  const checked = selectedValue === node.value;

  const handleClick = () => {
    const newChecked = !checked;
    const newOpenPaths = newChecked ? path : undefined;
    onSelect(node.value, newOpenPaths);
  };

  return (
    <div className={depth > 0 ? "border-l border-gray-200 ml-4" : ""}>
      {/* ROW */}
      <div
        className="flex items-center justify-between py-1.5 px-2 rounded-md cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={handleClick}
      >
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          {/* Radio-style checkbox */}
          <div
            className={` rounded  flex items-center justify-center flex-shrink-0 transition-colors ${checked ? "border-primary bg-primary" : "border-gray-400 bg-white"
              }`}
          >
            {checked ? <Check className="w-5 h-5 text-white" /> : <div className="border rounded-[4px] bg-white border-solid p-[9px] border-gray-400 h-2 w-2" />}
          </div>

          <span className="text-sm text-gray-800 truncate">
            {node.label}
          </span>
        </div>

        {/* Arrow */}
        {hasChildren && (
          <ChevronDown
            className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ${isOpen ? "rotate-180" : ""}`}
          />
        )}
      </div>

      {/* CHILDREN – SMOOTH OPEN/CLOSE */}
      <AnimatePresence initial={false}>
        {hasChildren && isOpen && (
          <motion.div
            layout
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="py-1">
              {node.subcategories!
                .sort((a, b) => (a.sortorder ?? 0) - (b.sortorder ?? 0))
                .map((child) => (
                  <CategoryTreeNodeMobile
                    key={child.value}
                    node={child}
                    path={[...path, child.value]}
                    depth={depth + 1}
                    selectedValue={selectedValue}
                    openPaths={openPaths}
                    onPathChange={onPathChange}
                    onSelect={onSelect}
                  />
                ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main Mobile Sheet                                                 */
/* ------------------------------------------------------------------ */
export default function MobileFilterSheet({
  isOpen,
  onOpenChange,
  filters,
  selectedFilters,
  onFilterChange,
  onClearAll,
  onApply,
  setCategorySlug,
}: MobileFilterSheetProps) {
  const [activeFilterCategory, setActiveFilterCategory] = useState<string>("category");
  const [tempFilters, setTempFilters] = useState<Record<string, string[]>>(selectedFilters);
  const [openPaths, setOpenPaths] = useState<string[]>([]); // mobile version of openPaths

  // Sync temp state when sheet opens
  useEffect(() => {
    if (isOpen) {
      setTempFilters(selectedFilters);
      const selectedCat = selectedFilters.category?.[0];
      if (selectedCat) {
        const path = findPathToNode(filters.category, selectedCat);
        setOpenPaths(path.map((_, i, arr) => arr.slice(0, i + 1).join("/")));
      } else {
        setOpenPaths([]);
      }
    }
  }, [isOpen, selectedFilters, filters.category]);

  /* ---------- Category selection (single) ---------- */
  const handleCategorySelect = useCallback(
    (value: string, newOpenPaths?: string[]) => {
      setTempFilters((prev) => ({
        ...prev,
        category: [value],
      }));

      if (newOpenPaths) {
        const newPathStr = newOpenPaths.join("/");
        const filtered = openPaths.filter((p) => {
          const parts = p.split("/");
          return parts.length < newOpenPaths.length || p === newPathStr;
        });
        setOpenPaths([...filtered, newPathStr]);
      } else {
        setOpenPaths((prev) => prev.filter((p) => !p.startsWith(value + "/") && p !== value));
      }
    },
    [openPaths]
  );

  /* ---------- Other filters (multi) ---------- */
  const handleOtherFilterChange = (filterType: string, value: string, checked: boolean) => {
    setTempFilters((prev) => {
      const current = prev[filterType] || [];
      return {
        ...prev,
        [filterType]: checked ? [...current, value] : current.filter((v) => v !== value),
      };
    });
  };

  /* ---------- Apply ---------- */
  const handleApply = () => {
    // Sync temp → parent
    Object.entries(tempFilters).forEach(([type, values]) => {
      values.forEach((v) => {
        if (!selectedFilters[type]?.includes(v)) onFilterChange(type, v, true);
      });
      (selectedFilters[type] || []).forEach((v) => {
        if (!values.includes(v)) onFilterChange(type, v, false);
      });
    });

    const cat = tempFilters.category?.[0];
    setCategorySlug(cat || filters.category[0]?.value || "");
    onApply();
    onOpenChange(false);
  };

  const handleClearAll = () => {
    setTempFilters({ category: [] });
    setOpenPaths([]);
    onClearAll();
    setCategorySlug(filters.category[0]?.value || "");
    onOpenChange(false);
  };

  /* ---------- Helpers ---------- */
  const findPathToNode = (nodes: FilterOption[], target: string, cur: string[] = []): string[] => {
    for (const n of nodes) {
      const next = [...cur, n.value];
      if (n.value === target) return next;
      if (n.subcategories) {
        const found = findPathToNode(n.subcategories, target, next);
        if (found.length) return found;
      }
    }
    return [];
  };

  const currentOptions = filters[activeFilterCategory] || [];

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[90vh] flex flex-col p-0">
        <SheetHeader className="flex flex-row items-center justify-between px-4 py-3 border-b">
          <SheetTitle className="text-lg font-semibold">Filters</SheetTitle>
          <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)}>
            <X className="w-5 h-5" />
            <span className="sr-only">Close</span>
          </Button>
        </SheetHeader>



        <div className="flex-1 overflow-y-auto p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            {activeFilterCategory === "category" ? (
              /* ---------- CATEGORY TREE (MOBILE) ---------- */
              <div className="space-y-1">
                {filters.category
                  .sort((a, b) => (a.sortorder ?? 0) - (b.sortorder ?? 0))
                  .map((opt) => (
                    <CategoryTreeNodeMobile
                      key={opt.value}
                      node={opt}
                      path={[opt.value]}
                      selectedValue={tempFilters.category?.[0] || null}
                      openPaths={openPaths}
                      onPathChange={setOpenPaths}
                      onSelect={handleCategorySelect}
                    />
                  ))}
              </div>
            ) : (
              /* ---------- OTHER FILTERS (CHECKBOXES) ---------- */
              <div className="grid gap-3">
                {currentOptions.map((opt: FilterOption) => (
                  <motion.div
                    key={opt.value}
                    className="flex items-center space-x-2"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Checkbox
                      id={`mobile-${activeFilterCategory}-${opt.value}`}
                      checked={tempFilters[activeFilterCategory]?.includes(opt.value) || false}
                      onCheckedChange={(c) =>
                        handleOtherFilterChange(activeFilterCategory, opt.value, c as boolean)
                      }
                    />
                    <label
                      htmlFor={`mobile-${activeFilterCategory}-${opt.value}`}
                      className="text-sm font-medium leading-none"
                    >
                      {opt.label} ({opt.count})
                    </label>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        </div>

        {/* Bottom Actions */}
        <div className="flex-shrink-0 flex border-t p-4 gap-2">
          <Button variant="outline" className="flex-1 bg-transparent" onClick={handleClearAll}>
            Clear All
          </Button>
          <Button className="flex-1 bg-primary text-white" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}